import gql from 'graphql-tag';

// ─── Real backend contract ──────────────────────────────────────────────────
// Field names/casing below (documentAIStatus, documentAIProgress, etc.) mirror the schema
// exactly as given - do not "clean up" the casing to match this app's own DocumentAi naming
// convention, that would break the request.

export enum DocumentAIProcessingStatus {
    Queued = 0,
    Extracting = 1,
    Embedding = 2,
    Analysing = 3,
    Ready = 4,
    Failed = 5,
    Cancelled = 6,
    Partial = 7,
}

export enum DocumentAIRequestMode {
    ProcessMissing = 0,
    ReuseExtraction = 1,
    RereadSource = 2,
}

// Deliberately a separate enum from DocumentAIProcessingStatus above - the backend's own
// enquiryAIAssessment/enquiryAIProgress use a distinct 5-value status (queued/processing
// documents/assessing/ready/failed), not the 8-value per-document pipeline enum. Reusing the
// document enum's numeric values here would misread assessment status 2 ("assessing") as the
// document pipeline's "embedding", etc.
export enum EnquiryAssessmentProcessingStatus {
    Queued = 0,
    ProcessingDocuments = 1,
    Assessing = 2,
    Ready = 3,
    Failed = 4,
}

// ─── Process documents ──────────────────────────────────────────────────────
// Processes selected documents, or every document on the enquiry when documentGuids is omitted
// (an empty array is invalid, so "process all" must omit the key entirely, not send []).
// Newly uploaded documents are auto-queued by createEnquiry/updateEnquiry - this mutation is
// only for the explicit "process with AI" trigger.

export interface ProcessEnquiryDocumentsInput {
    enquiryGuid: string;
    documentGuids?: string[];
    refresh: boolean;
}

export interface ProcessEnquiryDocumentsAIResult {
    processEnquiryDocumentsAI: {
        status: string;
        error: string | null;
    };
}

export interface ProcessEnquiryDocumentsAIParams {
    input: ProcessEnquiryDocumentsInput;
}

export const ProcessEnquiryDocumentsAIMutation = gql`
    mutation ProcessEnquiryDocumentsAI($input: ProcessEnquiryDocumentsInput!) {
        processEnquiryDocumentsAI(input: $input) {
            status
            error
        }
    }
`;

// ─── Assess / reassess the whole enquiry ───────────────────────────────────────
// Distinct from processEnquiryDocumentsAI above, which only processes individual documents'
// own extraction/analysis - these are what actually drive the enquiry-level assessment
// (enquiryAIAssessment below), and both "first ensure all current enquiry attachments have
// document results" server-side, so the UI doesn't need to sequence per-document processing
// before calling these itself. assessEnquiryAI reuses an existing assessment when nothing's
// changed (safe to call as the plain "Assess" action); reassessEnquiryAI always creates a fresh
// generation (used for the explicit "Reassess"/retry-after-failure action) - this maps directly
// onto this app's own existing refresh flag: refresh false -> assess, refresh true -> reassess.

export interface AssessEnquiryAIResult {
    assessEnquiryAI: {
        status: string;
        error: string | null;
    };
}

export interface ReassessEnquiryAIResult {
    reassessEnquiryAI: {
        status: string;
        error: string | null;
    };
}

export interface EnquiryAIAssessmentMutationParams {
    enquiryGuid: string;
}

export const AssessEnquiryAIMutation = gql`
    mutation AssessEnquiryAI($enquiryGuid: String!) {
        assessEnquiryAI(enquiryGuid: $enquiryGuid) {
            status
            error
        }
    }
`;

export const ReassessEnquiryAIMutation = gql`
    mutation ReassessEnquiryAI($enquiryGuid: String!) {
        reassessEnquiryAI(enquiryGuid: $enquiryGuid) {
            status
            error
        }
    }
`;

// ─── Status ──────────────────────────────────────────────────────────────────
// One row per document (documentGuid) per generation/revision - a "process all" call still
// produces one row per document, there's no single combined "enquiry" row for per-document
// status (see enquiryAIAssessment below for the real enquiry-level equivalent). Used both to
// seed state for documents that were already processing/already ready before this page load
// (e.g. from an auto-queued upload, or a previous session), and to read progress.
//
// Full field set per the current contract - this app's own UI only reads a subset of these
// (documentGuid/documentName/processingStatus/stage/isTerminal/completedUnits/totalUnits/
// hasResults/generation/revision/errorCode), but the rest are fetched too for fidelity to the
// contract as given, rather than second-guessing which fields might matter later. documentAIStatus
// intentionally omits documents never submitted to AI - enquiry.detail's own `files` collection
// stays the authoritative list, and a document with no matching row here means "not processed",
// not "no data yet".
export interface DocumentAIStatusRecord {
    processingGuid: string;
    operationGuid: string;
    entityType: string;
    entityGuid: string;
    documentGuid: string | null;
    documentName: string | null;
    generation: number;
    revision: number;
    requestMode: DocumentAIRequestMode;
    processingStatus: DocumentAIProcessingStatus;
    stage: string | null;
    isTerminal: boolean;
    completedUnits: number | null;
    totalUnits: number | null;
    batchTotal: number | null;
    batchCompleted: number | null;
    batchFailed: number | null;
    hasResults: boolean;
    resultProcessingGuid: string | null;
    createdAtUtc: string;
    updatedAtUtc: string;
    completedAtUtc: string | null;
    errorCode: string | null;
}

export interface GetEnquiryDocumentAIStatusData {
    crm: {
        documentAIStatus: DocumentAIStatusRecord[];
    };
}

export interface GetEnquiryDocumentAIStatusParams {
    enquiryGuid: string;
}

export const GetEnquiryDocumentAIStatusQuery = gql`
    query GetEnquiryDocumentAIStatus($enquiryGuid: String!) {
        crm {
            documentAIStatus(enquiryGuid: $enquiryGuid) {
                processingGuid
                operationGuid
                entityType
                entityGuid
                documentGuid
                documentName
                generation
                revision
                requestMode
                processingStatus
                stage
                isTerminal
                completedUnits
                totalUnits
                batchTotal
                batchCompleted
                batchFailed
                hasResults
                resultProcessingGuid
                createdAtUtc
                updatedAtUtc
                completedAtUtc
                errorCode
            }
        }
    }
`;

// ─── Enquiry-level assessment status ───────────────────────────────────────────
// The real enquiry-level equivalent of a per-document status row - documentsTotal/Completed/
// Failed and progressPercentage are computed server-side, so the UI no longer needs to roll one
// up from the individual per-document rows itself. Null means the enquiry has never been
// assessed. While an INCREMENTAL assessEnquiryAI run is in progress, hasResults can stay true
// (the previous result stays viewable throughout). A FULL reassessEnquiryAI run instead clears
// hasResults/resultAssessmentGuid/assessmentJson/citationsJson until its own new result is saved
// - it deliberately hides the earlier report while refreshing, rather than leaving it viewable.
export interface EnquiryAIAssessmentRecord {
    assessmentGuid: string;
    operationGuid: string;
    entityType: string;
    entityGuid: string;
    generation: number;
    revision: number;
    requestMode: DocumentAIRequestMode;
    processingStatus: EnquiryAssessmentProcessingStatus;
    stage: string | null;
    progressPercentage: number | null;
    // Backend-composed human-readable progress text (e.g. "Analysing document 3 of 5...") -
    // shown as the primary detail line; never reconstructed client-side from the raw counters
    // below, which are only ever a secondary/supplementary readout.
    progressMessage: string | null;
    // True while progressPercentage is a coarse milestone, not measured completion (the combined
    // "Assessing" call in particular parks at 85% until it finishes) - render an indeterminate
    // indicator instead of implying the number is advancing.
    isProgressIndeterminate: boolean;
    // Simultaneous per-attachment work counts, not sequential UI steps - several documents can
    // be extracting/analysing/reviewing at the same time.
    documentsExtracting: number | null;
    documentsAnalysing: number | null;
    documentsReviewing: number | null;
    // Completed passage analyses (not inserted vectors or PDF pages). passagesTotal is null
    // until every attachment has a known passage count and is never substituted with 0 or a
    // guess - show only passagesCompleted while it's null. Both are null outside document
    // processing (including the final combined call and every terminal state).
    passagesCompleted: number | null;
    passagesTotal: number | null;
    // Last committed work/stage transition, not a heartbeat - the basis for an elapsed-time
    // readout while the backend is otherwise quiet (e.g. "2m elapsed in this stage" during the
    // final combined call), not proof of a stall.
    lastProgressAtUtc: string | null;
    documentsTotal: number | null;
    documentsCompleted: number | null;
    documentsFailed: number | null;
    isTerminal: boolean;
    hasResults: boolean;
    resultAssessmentGuid: string | null;
    // Query-only: detects a changed enquiry/attachment/finding set or a failed document by
    // comparing the current authorised snapshot - null while processing or on a subscription
    // snapshot (neither carries a meaningful answer), so only ever read from a fresh reload of
    // this query, never assumed from a cached/optimistic session.
    needsAssessment: boolean | null;
    assessmentJson: string | null;
    citationsJson: string | null;
    createdAtUtc: string;
    updatedAtUtc: string;
    completedAtUtc: string | null;
    errorCode: string | null;
}

export interface GetEnquiryAIAssessmentData {
    crm: {
        enquiryAIAssessment: EnquiryAIAssessmentRecord | null;
    };
}

export interface GetEnquiryAIAssessmentParams {
    enquiryGuid: string;
}

export const GetEnquiryAIAssessmentQuery = gql`
    query GetEnquiryAIAssessment($enquiryGuid: String!) {
        crm {
            enquiryAIAssessment(enquiryGuid: $enquiryGuid) {
                assessmentGuid
                operationGuid
                entityType
                entityGuid
                generation
                revision
                requestMode
                processingStatus
                stage
                progressPercentage
                progressMessage
                isProgressIndeterminate
                documentsExtracting
                documentsAnalysing
                documentsReviewing
                passagesCompleted
                passagesTotal
                lastProgressAtUtc
                documentsTotal
                documentsCompleted
                documentsFailed
                isTerminal
                hasResults
                resultAssessmentGuid
                needsAssessment
                assessmentJson
                citationsJson
                createdAtUtc
                updatedAtUtc
                completedAtUtc
                errorCode
            }
        }
    }
`;

// ─── Results ─────────────────────────────────────────────────────────────────
// One query covers both "all available results" (documentGuid omitted) and "one document's
// result" (documentGuid passed) - same field, same shape, just an optional extra filter.

export interface DocumentAIResultRecord {
    documentGuid: string;
    resultProcessingGuid: string;
    hasResults: boolean;
    analysisText: string;
}

export interface GetEnquiryDocumentAIResultsData {
    crm: {
        documentAIResults: DocumentAIResultRecord[];
    };
}

export interface GetEnquiryDocumentAIResultsParams {
    enquiryGuid: string;
    documentGuid?: string;
}

export const GetEnquiryDocumentAIResultsQuery = gql`
    query GetEnquiryDocumentAIResults($enquiryGuid: String!, $documentGuid: String) {
        crm {
            documentAIResults(enquiryGuid: $enquiryGuid, documentGuid: $documentGuid) {
                documentGuid
                resultProcessingGuid
                hasResults
                analysisText
            }
        }
    }
`;

// ─── Progress subscription ───────────────────────────────────────────────────
// One subscription for the whole app - filtered server-side to the signed-in user/tenant, no
// params - matches this app's own "implicitly scoped" convention elsewhere (e.g.
// EnquiryQuickViewCounts) and is what a single global AiDocumentChatProvider needs, since it can
// have sessions open across several different enquiries at once. Use `revision` to drop
// duplicate/out-of-order events for the same processingGuid.

export interface DocumentAIProgressEvent {
    message: string | null;
    description: string | null;
    status: string | null;
    processingGuid: string;
    operationGuid: string;
    entityType: string;
    entityGuid: string;
    documentGuid: string | null;
    documentName: string | null;
    generation: number;
    revision: number;
    requestMode: DocumentAIRequestMode;
    processingStatus: DocumentAIProcessingStatus;
    stage: string | null;
    isTerminal: boolean;
    completedUnits: number | null;
    totalUnits: number | null;
    batchTotal: number | null;
    batchCompleted: number | null;
    batchFailed: number | null;
    hasResults: boolean;
    resultProcessingGuid: string | null;
    createdAtUtc: string;
    updatedAtUtc: string;
    completedAtUtc: string | null;
    errorCode: string | null;
    /** Already a friendly, customer-safe failure message per the backend's own guidance - safe to
     * show directly (unlike errorCode, which stays machine-readable reference text only). */
    errorMessage: string | null;
}

export interface DocumentAIProgressSubscriptionData {
    documentAIProgress: DocumentAIProgressEvent;
}

export const DocumentAIProgressSubscription = gql`
    subscription DocumentAIProgress {
        documentAIProgress {
            message
            description
            status
            processingGuid
            operationGuid
            entityType
            entityGuid
            documentGuid
            documentName
            generation
            revision
            requestMode
            processingStatus
            stage
            isTerminal
            completedUnits
            totalUnits
            batchTotal
            batchCompleted
            batchFailed
            hasResults
            resultProcessingGuid
            createdAtUtc
            updatedAtUtc
            completedAtUtc
            errorCode
            errorMessage
        }
    }
`;

// ─── Enquiry assessment progress subscription ──────────────────────────────────
// Separate stream from documentAIProgress above - per-document progress never carries enquiry-
// level assessment updates, so without subscribing to this too the assessment session would
// only ever reflect whatever ensureStatusChecked saw at the moment the enquiry was opened.

export interface EnquiryAIProgressEvent {
    message: string | null;
    description: string | null;
    status: string | null;
    assessment: {
        assessmentGuid: string;
        entityGuid: string;
        generation: number;
        revision: number;
        processingStatus: EnquiryAssessmentProcessingStatus;
        stage: string | null;
        progressPercentage: number | null;
        progressMessage: string | null;
        isProgressIndeterminate: boolean;
        documentsExtracting: number | null;
        documentsAnalysing: number | null;
        documentsReviewing: number | null;
        passagesCompleted: number | null;
        passagesTotal: number | null;
        lastProgressAtUtc: string | null;
        documentsTotal: number | null;
        documentsCompleted: number | null;
        documentsFailed: number | null;
        isTerminal: boolean;
        hasResults: boolean;
        resultAssessmentGuid: string | null;
        createdAtUtc: string;
        updatedAtUtc: string;
        completedAtUtc: string | null;
        errorCode: string | null;
    } | null;
}

export interface EnquiryAIProgressSubscriptionData {
    enquiryAIProgress: EnquiryAIProgressEvent;
}

export const EnquiryAIProgressSubscription = gql`
    subscription EnquiryAIProgress {
        enquiryAIProgress {
            message
            description
            status
            assessment {
                assessmentGuid
                operationGuid
                entityType
                entityGuid
                generation
                revision
                requestMode
                processingStatus
                stage
                progressPercentage
                progressMessage
                isProgressIndeterminate
                documentsExtracting
                documentsAnalysing
                documentsReviewing
                passagesCompleted
                passagesTotal
                lastProgressAtUtc
                documentsTotal
                documentsCompleted
                documentsFailed
                isTerminal
                hasResults
                resultAssessmentGuid
                createdAtUtc
                updatedAtUtc
                completedAtUtc
                errorCode
            }
        }
    }
`;

// ─── Ask a question about processed documents ──────────────────────────────────
// Deliberately a query, not a mutation, because the UI waits synchronously for the answer.
// operationGuid must be a fresh client-generated guid per new question - it's only reused to
// retry the identical question (the server then returns its saved answer instead of billing a
// second provider call), which this app doesn't currently do (every send generates a new one).
// documentGuid omitted searches every current, successfully processed attachment.

// The full field set per the current contract - chunkGuid/processingGuid/ordinal/startOffset are
// fetched but kept as internal traceability fields only; the UI (AiChatDialog.tsx's citation
// chips) displays just documentName, the page range and excerpt, and must never label the others
// as "chunks" or otherwise surface them to the customer.
export interface DocumentAICitation {
    chunkGuid: string;
    processingGuid: string;
    documentGuid: string;
    documentName: string;
    ordinal: number;
    startPageNumber: number | null;
    endPageNumber: number | null;
    startOffset: number | null;
    excerpt: string;
}

export interface DocumentAIQuestionRecord {
    interactionGuid: string;
    operationGuid: string;
    enquiryGuid: string;
    documentGuid: string | null;
    question: string;
    answer: string;
    createdAtUtc: string;
    citations: DocumentAICitation[];
}

export interface DocumentAIQuestionData {
    crm: {
        documentAIQuestion: DocumentAIQuestionRecord;
    };
}

export interface DocumentAIQuestionParams {
    enquiryGuid: string;
    operationGuid: string;
    documentGuid?: string | null;
    question: string;
}

export const DocumentAIQuestionQuery = gql`
    query DocumentAIQuestion($enquiryGuid: String!, $operationGuid: String!, $documentGuid: String, $question: String!) {
        crm {
            documentAIQuestion(enquiryGuid: $enquiryGuid, operationGuid: $operationGuid, documentGuid: $documentGuid, question: $question) {
                interactionGuid
                operationGuid
                enquiryGuid
                documentGuid
                question
                answer
                createdAtUtc
                citations {
                    chunkGuid
                    processingGuid
                    documentGuid
                    documentName
                    ordinal
                    startPageNumber
                    endPageNumber
                    startOffset
                    excerpt
                }
            }
        }
    }
`;

// ─── Saved chat history ─────────────────────────────────────────────────────────
// For rendering only - this is UI-facing history stored in the tenant database (with a
// retention expiry), not AI conversation memory the provider itself sees. status: 0 processing,
// 1 answered, 2 failed. Fetched once per session when its dialog first opens (see
// ensureChatHistoryLoaded in AiDocumentChatContext.tsx), not on every enquiry-open hydration.

export interface DocumentAIInteractionRecord {
    interactionGuid: string;
    operationGuid: string;
    entityGuid: string;
    entityType: string;
    documentGuid: string | null;
    userID: string;
    question: string;
    response: string | null;
    status: number;
    errorCode: string | null;
    createdAtUtc: string;
    expiresAtUtc: string | null;
    citations: DocumentAICitation[];
}

export interface DocumentAIInteractionsData {
    crm: {
        documentAIInteractions: DocumentAIInteractionRecord[];
    };
}

export interface DocumentAIInteractionsParams {
    enquiryGuid: string;
    documentGuid?: string | null;
    take?: number;
}

export const DocumentAIInteractionsQuery = gql`
    query DocumentAIInteractions($enquiryGuid: String!, $documentGuid: String, $take: Int) {
        crm {
            documentAIInteractions(enquiryGuid: $enquiryGuid, documentGuid: $documentGuid, take: $take) {
                interactionGuid
                operationGuid
                entityGuid
                entityType
                documentGuid
                userID
                question
                response
                status
                errorCode
                createdAtUtc
                expiresAtUtc
                citations {
                    chunkGuid
                    processingGuid
                    documentGuid
                    documentName
                    ordinal
                    startPageNumber
                    endPageNumber
                    startOffset
                    excerpt
                }
            }
        }
    }
`;

export const documentAiProgressPercent = (completedUnits: number | null, totalUnits: number | null): number | undefined => (
    totalUnits && totalUnits > 0 ? Math.round(((completedUnits ?? 0) / totalUnits) * 100) : undefined
);
